import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb, faUserCheck, faCubes, faHouseLaptop, faLanguage, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import './AboutUs.css';

// Optional founder photo: put it in public/assets/img/about/ (leave empty to show initials)
const FOUNDER_PHOTO = '/assets/img/about/nadim.jpg';

const FALLBACK_STATS = { students: 300, classes: 7900 };
const roundDown = (value, step) => Math.floor(Number(value) / step) * step;
const formatPlus = (n) => `${n.toLocaleString('en-US')}+`;

const VALUES = [
  {
    icon: faLightbulb,
    title: 'Creators, not just users',
    text: 'Kids don’t only learn about technology. They build games, apps, websites, robots and AI projects of their own.',
  },
  {
    icon: faUserCheck,
    title: 'One child, one instructor',
    text: 'Every class is live and 1-to-1, so the pace, the projects and the explanations fit your child.',
  },
  {
    icon: faCubes,
    title: 'A project every session',
    text: 'Each session follows concept, experiment, project, result, and ends with something to save and show.',
  },
  {
    icon: faHouseLaptop,
    title: 'Safe, from home',
    text: 'Online classes with a familiar instructor, at times that fit around school, from anywhere.',
  },
];

const AboutUs = () => {
  const [stats, setStats] = useState(FALLBACK_STATS);

  // Same live numbers as the homepage hero
  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/class-stats`);
        if (!res.ok) return;
        const data = await res.json();
        const students = roundDown(data.studentsEnrolled, 50);
        const classes = roundDown(data.classesCompleted, 100);
        if (!cancelled && students > 0 && classes > 0) setStats({ students, classes });
      } catch (err) {
        // keep fallback numbers
      }
    };
    fetchStats();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="rmabout" aria-labelledby="rmabout-title">
      <div className="rmabout-inner">
        {/* Header */}
        <header className="rmabout-header">
          <span className="rmabout-eyebrow">About RoboMind</span>
          <h1 id="rmabout-title" className="rmabout-title">
            Helping kids become <span>creators of technology</span>
          </h1>
          <p className="rmabout-sub">
            RoboMind is an online academy for kids and teens aged 6–17. We teach coding, AI and robotics through live
            1-to-1 classes where every child learns by building real projects.
          </p>
        </header>

        {/* Stats */}
        <div className="rmabout-stats">
          <div>
            <strong>{formatPlus(stats.students)}</strong>
            <span>students enrolled</span>
          </div>
          <div>
            <strong>{formatPlus(stats.classes)}</strong>
            <span>live classes completed</span>
          </div>
          <div>
            <strong>Lebanon</strong>
            <span>&amp; families abroad</span>
          </div>
          <div className="rmabout-lang">
            <FontAwesomeIcon icon={faLanguage} />
            <span>Arabic · French · English</span>
          </div>
        </div>

        {/* Story */}
        <div className="rmabout-story">
          <div className="rmabout-founder">
            {FOUNDER_PHOTO ? (
              <img src={FOUNDER_PHOTO} alt="Nadim Nasrallah, founder of RoboMind" className="rmabout-founder-photo" />
            ) : (
              <span className="rmabout-founder-initials" aria-hidden="true">
                NN
              </span>
            )}
            <div>
              <strong>Nadim Nasrallah</strong>
              <span>Founder &amp; CEO · Full-stack developer, STEM &amp; AI education specialist</span>
            </div>
          </div>

          <div className="rmabout-story-text">
            <h2 className="rmabout-h2">Our story</h2>
            <p>
              RoboMind was built from scratch, from the curriculum and hundreds of project sessions to the platform
              students learn on. It started with one instructor teaching the first students himself, with a simple
              idea: kids learn technology best by creating with it, with someone guiding them one-to-one.
            </p>
            <p>
              Today a team of instructors teaches the same structured curriculum, so every child gets consistent
              quality and personal attention, whether they live in Lebanon or abroad.
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="rmabout-values-wrap">
          <h2 className="rmabout-h2">What we believe</h2>
          <div className="rmabout-values">
            {VALUES.map((v) => (
              <div key={v.title} className="rmabout-value">
                <span className="rmabout-value-icon">
                  <FontAwesomeIcon icon={v.icon} />
                </span>
                <div>
                  <h3>{v.title}</h3>
                  <p>{v.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rmabout-links">
          <Link to="/Programs" className="rmabout-link">
            Explore our courses
            <FontAwesomeIcon icon={faArrowRight} />
          </Link>
          <Link to="/Curriculum" className="rmabout-link rmabout-link--ghost">
            How we teach
            <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;